import { Toast as BaseToast } from '@base-ui/react';
import { FC, ReactNode } from 'react';

import AlertCircleRed from '@/assets/svg/ui-elements/alert-circle-red.svg';
import AlertTriangleOutlineYellow from '@/assets/svg/ui-elements/alert-triangle-outline-yellow.svg';
import CheckCircleGreen from '@/assets/svg/ui-elements/check-circle-green.svg';
import CloseIcon from '@/assets/svg/ui-elements/close.svg';
import InfoCircleOutlineBlue from '@/assets/svg/ui-elements/info-circle-outline-blue.svg';

import Button from '../Button';
import {
  CloseButton,
  StyledActions,
  StyledContainer,
  StyledContent,
  StyledDescription,
  StyledRoot,
  StyledTitle,
  StyledViewport,
  ToastIconWrapper,
  ToastTexts,
} from './Toast.styled';
import type { ToastData, ToastStatus } from './types';

const STATUS_ICON: Record<ToastStatus, ReactNode> = {
  info: <InfoCircleOutlineBlue />,
  warning: <AlertTriangleOutlineYellow />,
  error: <AlertCircleRed />,
  success: <CheckCircleGreen />,
};

const ToastList = () => {
  const { toasts, close } = BaseToast.useToastManager<ToastData>();

  return toasts.map((toast) => {
    const isSmall = toast.data?.size === 'sm';
    const actionLabel = isSmall ? undefined : toast.data?.actionLabel;
    const onAction = toast.data?.onAction;
    const icon = toast.data?.icon ?? (toast.data?.status ? STATUS_ICON[toast.data.status] : null);

    return (
      <StyledRoot key={toast.id} toast={toast} swipeDirection="up">
        <StyledContent>
          {icon && <ToastIconWrapper>{icon}</ToastIconWrapper>}

          <StyledContainer>
            <ToastTexts>
              {toast.title && <StyledTitle>{toast.title}</StyledTitle>}
              {toast.description && !isSmall && (
                <StyledDescription>{toast.description}</StyledDescription>
              )}
            </ToastTexts>

            {actionLabel && (
              <StyledActions>
                <Button size="sm" onClick={onAction}>
                  {actionLabel}
                </Button>
              </StyledActions>
            )}
          </StyledContainer>

          <CloseButton onClick={() => close(toast.id)} aria-label="Dismiss notification">
            <CloseIcon />
          </CloseButton>
        </StyledContent>
      </StyledRoot>
    );
  });
};

export const useToast = () => BaseToast.useToastManager<ToastData>();

const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => (
  <BaseToast.Provider>
    <BaseToast.Portal>
      <StyledViewport>
        <ToastList />
      </StyledViewport>
    </BaseToast.Portal>
    {children}
  </BaseToast.Provider>
);

export default ToastProvider;

ToastProvider.displayName = 'ToastProvider';
